import { useMemo, useEffect } from 'react';
import { graphql, type HeadProps, type PageProps } from 'gatsby';
import { useLocation } from '@reach/router';
import Layout from '../components/Layout';
import MetaTags from '@/components/MetaTags/MetaTags';
import { SearchContextProvider } from '@/context/SearchContext';
import useABTest from '@/hooks/useABTest';
import { isPageTranslated } from '@/utils/localization';
import sendABTestAnalytics from '@/utils/a-b-test/sendABTestAnalytics';
import abTestComponentsConfigRaw from '../../netlify/edge-functions/lib/a-b-test-component.config.json';
import type { ABTestComponentConfig } from '../../netlify/edge-functions/lib/types';
import usePersonalization from '@/utils/component-personalization/usePersonalization';
import { useSendPersonalizationAnalytics } from '@/utils/component-personalization/useSendPersonalizationAnalytics';

const abTestComponentsConfig = abTestComponentsConfigRaw as ABTestComponentConfig[];

interface IndexPageTemplateInterface extends PageProps<Queries.IndexPageTemplateQuery> {
  pageContext: {
    postsCategories: string;
  };
}

const IndexPageTemplate = ({ data, pageContext, location }: IndexPageTemplateInterface) => {
  const { pathname } = useLocation();
  const availableLanguages = data.allKontentItemIndexPage.group.flatMap((item) => item.distinct);

  // Memoize filtered component configs to prevent unnecessary recalculations
  const filteredComponentsConfig = useMemo(
    () => abTestComponentsConfig.filter((config) => config.controlPaths.includes(pathname)),
    [pathname]
  );

  const { elements, abTestId, abTestVariantId, abTestComponents } = useABTest({
    page: data.allKontentItemIndexPage.nodes[0],
    processedABTestConfig: [],
    processedABTestComponentsConfig: filteredComponentsConfig,
  });

  useEffect(() => {
    sendABTestAnalytics({
      abTestId,
      abTestVariantId,
      abTestComponents,
    });
  }, [abTestId, abTestVariantId, abTestComponents]);

  const hasAbTest = !!(abTestId && abTestVariantId) || abTestComponents.length > 0;
  const {
    elements: personalizedElements,
    attributes,
    winningCampaigns,
  } = usePersonalization(data.allKontentItemIndexPage.nodes[0]);

  useSendPersonalizationAnalytics({
    hasAbTest,
    winningCampaigns,
    attributes,
  });

  return (
    <SearchContextProvider category={pageContext.postsCategories} location={location}>
      <Layout
        pageContext={pageContext}
        elements={hasAbTest ? elements : personalizedElements}
        data={data}
        availableLanguages={availableLanguages}
      />
    </SearchContextProvider>
  );
};

export const Head = ({
  data,
  pageContext,
}: HeadProps<Queries.IndexPageTemplateQuery, PageContext>) => {
  const isTranslated = isPageTranslated({
    preferredLanguage: data.allKontentItemIndexPage.nodes[0].preferred_language,
    pageLanguage: data.allKontentItemIndexPage.nodes[0].system.language,
  });

  return (
    <>
      <MetaTags
        language={pageContext.language}
        metadata={data.allKontentItemIndexPage.nodes[0].elements}
        hasRssFeed={data.allKontentItemIndexPage.nodes[0].elements.rss_feed?.value === 'on'}
        shouldNotIndex={!isTranslated}
        isTranslated={isTranslated}
      />
      <link crossOrigin="anonymous" href="https://WY2821OY3G-dsn.algolia.net" rel="preconnect" />
      {data.allKontentItemArticlePage.pageInfo.currentPage > 1 ? (
        <meta name="robots" content="noindex, follow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
    </>
  );
};

export const query = graphql`
  query IndexPageTemplate(
    $pageCodename: String!
    $postsCategories: [String!]
    $postsPerPage: Int!
    $postsOffset: Int!
    $language: String!
  ) {
    allKontentItemIndexPage(
      filter: { preferred_language: { eq: $language }, system: { codename: { eq: $pageCodename } } }
    ) {
      nodes {
        __typename
        elements {
          ...TWITTER_META
          ...OPENGRAPH_META
          ...SEO_META
          ...PAGE_LAYOUT
          building_blocks {
            value {
              ... on kontent_item {
                system {
                  id
                  type
                }
              }
              ...ACCORDION
              ...ARTICLE_POST
              ...CARDS_BLOCK
              ...CAROUSEL_SECTION
              ...COLUMN_BLOCK
              ...COMBO_BLOCK
              ...COMPARISON_CARDS
              ...COMPARISON_TABLE
              ...CTACARD
              ...CTA_BLOCK
              ...EXIT_INTENT_POPUP
              ...FOOTER
              ...FORM_CONTAINER
              ...HEADER
              ...HERO_BLOCK_ARTICLE_DETAIL_1
              ...HERO_BLOCK_ARTICLE_DETAIL_2
              ...HERO_BLOCK_HOMEPAGE
              ...HERO_BLOCK_INDEX
              ...HERO_EMAIL_FORM
              ...IFRAME
              ...IMAGE_COLUMN_BLOCK
              ...INDUSTRY_LOGO_BLOCK
              ...INTERSTITIAL
              ...JUMP_TO_MENU
              ...LANGUAGE_GRID_BLOCK
              ...LANGUAGE_GRID_BLOCK_V2
              ...LOGO_BLOCK
              ...MEDIA_CAROUSEL
              ...MID_PAGE_CTA
              ...MID_PAGE_FORM_CONTAINER
              ...MID_PAGE_SEARCH
              ...MODAL
              ...N5050_BLOCK
              ...N5050_CARD_BLOCK
              ...OPEN_SOURCE_SOLUTIONS
              ...QUOTE_BLOCK
              ...SEARCH_BLOCK
              ...SECONDARY_HERO_BLOCK
              ...SECONDARY_NAVIGATION
              ...SECTION_INTRODUCTION
              ...SINGLE_MEDIA
              ...STATBAR_BLOCK
              ...STATBAR_BLOCK_V2
              ...STICKY_SIDEBAR
              ...SUPER_CTA_BLOCK
              ...TABBED_BLOCK
              ...TERTIARY_NAVIGATION
              ...TESTIMONIAL_BLOCK
              ...TIMELINE_BLOCK
              ...TOGGLE_RESOURCES
              ...VULNERABILITY_DISCLOSURE_TABLE
            }
          }
          rss_feed {
            value
          }
        }
        preferred_language
        system {
          language
        }
      }
      group(field: { system: { language: SELECT } }) {
        distinct(field: { system: { language: SELECT } })
      }
    }
    allKontentItemArticlePage(
      filter: {
        preferred_language: { eq: $language }
        elements: {
          category: { value: { elements: { name: { value: { in: $postsCategories } } } } }
          display_on_index_page: { value: { eq: yes } }
        }
      }
      limit: $postsPerPage
      skip: $postsOffset
      sort: { elements: { creation_date: { value: DESC } } }
    ) {
      nodes {
        ...POST_GRID_BLOCK
      }
      pageInfo {
        currentPage
        pageCount
      }
    }
  }
`;

export default IndexPageTemplate;
